import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, GripVertical, Image, FileVideo } from 'lucide-react';
import { uploadFile } from '@/lib/utils';

export interface Brief {
  id?: string;
  campaign_id?: string;
  brief_type: 'required' | 'open';
  title: string;
  description: string;
  thumbnail_url: string;
  example_video_url: string;
  sort_order: number;
  _errors?: Record<string, boolean>;
}

interface VideoBriefsProps {
  briefs: Brief[];
  onChange: (briefs: Brief[]) => void;
}

const MAX_BRIEFS = 5;

export function validateBriefs(briefs: Brief[]): { valid: boolean; briefs: Brief[] } {
  let valid = true;
  const validated = briefs.map(b => {
    if (b.brief_type === 'open') return { ...b, _errors: undefined };
    const errors: Record<string, boolean> = {};
    if (!b.title?.trim()) { errors.title = true; valid = false; }
    if (!b.thumbnail_url?.trim()) { errors.thumbnail_url = true; valid = false; }
    if (!b.example_video_url?.trim()) { errors.example_video_url = true; valid = false; }
    return { ...b, _errors: Object.keys(errors).length ? errors : undefined };
  });
  return { valid, briefs: validated };
}

export default function VideoBriefs({ briefs, onChange }: VideoBriefsProps) {
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  const fileRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const addBrief = (type: 'required' | 'open') => {
    if (briefs.length >= MAX_BRIEFS) return;
    onChange([...briefs, {
      brief_type: type,
      title: '',
      description: '',
      thumbnail_url: '',
      example_video_url: '',
      sort_order: briefs.length,
    }]);
  };

  const updateBrief = (idx: number, patch: Partial<Brief>) => {
    const updated = briefs.map((b, i) => i === idx ? { ...b, ...patch, _errors: undefined } : b);
    onChange(updated);
  };

  const removeBrief = (idx: number) => {
    const updated = briefs.filter((_, i) => i !== idx).map((b, i) => ({ ...b, sort_order: i }));
    onChange(updated);
  };

  const handleThumbnailUpload = async (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const fileName = `brief-thumb-${Date.now()}.${file.name.split('.').pop()}`;
      const url = await uploadFile(file, 'brief-assets', fileName);
      updateBrief(idx, { thumbnail_url: url });
      toast.success('Thumbnail uploaded');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploadingIdx(null);
      const ref = fileRefs.current[idx];
      if (ref) ref.value = '';
    }
  };

  // Simple drag reorder
  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const reordered = [...briefs];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    onChange(reordered.map((b, i) => ({ ...b, sort_order: i })));
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const atMax = briefs.length >= MAX_BRIEFS;

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Video Briefs</h3>
          <p className="text-xs text-muted-foreground">{briefs.length} of {MAX_BRIEFS} briefs configured</p>
        </div>
      </div>

      <div className="space-y-3">
        {briefs.map((brief, idx) => (
          <Card
            key={idx}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
            className={`transition-opacity ${dragIdx === idx ? 'opacity-50' : ''} ${brief.brief_type === 'open' ? 'bg-muted/50' : ''}`}
          >
            <CardContent className="p-3">
              {brief.brief_type === 'open' ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                    <Badge variant="secondary">Open Brief</Badge>
                    <span className="text-sm text-muted-foreground">— creator's choice</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeBrief(idx)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      <Badge>Required</Badge>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeBrief(idx)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>

                  <div>
                    <Label className="text-xs">Title *</Label>
                    <Input
                      value={brief.title}
                      onChange={e => updateBrief(idx, { title: e.target.value })}
                      placeholder="Brief title"
                      className={brief._errors?.title ? 'border-destructive' : ''}
                    />
                    {brief._errors?.title && <p className="text-xs text-destructive mt-1">Title is required</p>}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Thumbnail *</Label>
                    <input
                      ref={el => { fileRefs.current[idx] = el; }}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => handleThumbnailUpload(idx, e)}
                    />
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingIdx === idx}
                        onClick={() => fileRefs.current[idx]?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {uploadingIdx === idx ? 'Uploading...' : 'Upload'}
                      </Button>
                      {brief.thumbnail_url && (
                        <a href={brief.thumbnail_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline flex items-center gap-1">
                          <Image className="h-3 w-3" /> View
                        </a>
                      )}
                    </div>
                    {brief.thumbnail_url && (
                      <img src={brief.thumbnail_url} alt="Thumbnail" className="h-16 w-16 rounded border object-cover mt-1" />
                    )}
                    {brief._errors?.thumbnail_url && <p className="text-xs text-destructive mt-1">Thumbnail is required</p>}
                  </div>

                  <div>
                    <Label className="text-xs">Example Video URL *</Label>
                    <Input
                      value={brief.example_video_url}
                      onChange={e => updateBrief(idx, { example_video_url: e.target.value })}
                      placeholder="https://www.tiktok.com/..."
                      className={brief._errors?.example_video_url ? 'border-destructive' : ''}
                    />
                    {brief._errors?.example_video_url && <p className="text-xs text-destructive mt-1">Example video URL is required</p>}
                  </div>

                  <div>
                    <Label className="text-xs">Description (optional)</Label>
                    <Textarea
                      value={brief.description}
                      onChange={e => updateBrief(idx, { description: e.target.value })}
                      rows={2}
                      placeholder="Describe what this brief covers..."
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={atMax}
          onClick={() => addBrief('required')}
          className="bg-pink-600 hover:bg-pink-700 text-white"
        >
          <Plus className="mr-1 h-4 w-4" />
          {atMax ? 'Maximum 5 briefs reached' : 'Add Required Brief'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={atMax}
          onClick={() => addBrief('open')}
        >
          <Plus className="mr-1 h-4 w-4" />
          {atMax ? 'Max reached' : 'Add Open Brief'}
        </Button>
      </div>
    </div>
  );
}
